import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageNotificationService } from '../../../shared/services/message-notification.service';
import { ActionCableRepository } from '../../../shared/repositories/action-cable.repository';
import {
  MessageNotification,
  MessageNotifications,
  RepairMessageNotification
} from '../../declarations/message-notifications.interface';
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  from,
  map,
  switchMap,
  takeUntil
} from 'rxjs';
import {
  SetMessageNotifications,
  SubscribeToBENotifications,
  UnsubscribeFromBENotifications
} from './message-notifications.actions';
import { PushNotificationService } from '../../../shared/services/push-notification.service';

@Injectable()
export class MessageNotificationsEffects {
  loadMessageNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscribeToBENotifications),
      switchMap(() => {
        return this.messageNotificationService
          .messageNotificationWSUpdates()
          .pipe(
            this.actionCableRepository.reconnectionHandler(),
            takeUntil(
              this.actions$.pipe(ofType(UnsubscribeFromBENotifications))
            ),
            debounceTime(500),
            map((newNotifications: MessageNotifications) => {
              const newNotificationsWithCount = {
                services: {
                  count: this.sumNotificationsByType(
                    newNotifications.repair_message_notifications
                  ),
                  notifications: newNotifications.repair_message_notifications
                },
                community: {
                  count: this.sumNotificationsByType(
                    newNotifications.conversation_notifications
                  ),
                  notifications: newNotifications.conversation_notifications
                }
              };
              return SetMessageNotifications({
                notifications: newNotificationsWithCount
              });
            }),
            catchError(() => EMPTY)
          );
      })
    )
  );

  syncUnreadIndicator$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SetMessageNotifications),
        map(
          ({ notifications }) =>
            notifications.services.count + notifications.community.count
        ),
        distinctUntilChanged(),
        concatMap(unreadCount =>
          from(
            this.pushNotificationService.setUnreadIndicator(unreadCount)
          ).pipe(catchError(() => EMPTY))
        )
      ),
    { dispatch: false }
  );

  private sumNotificationsByType(
    notifications: (RepairMessageNotification | MessageNotification)[]
  ): number {
    return notifications.reduce(
      (total, notification) => total + notification.notification_count,
      0
    );
  }

  constructor(
    private actions$: Actions,
    private messageNotificationService: MessageNotificationService,
    private actionCableRepository: ActionCableRepository,
    private pushNotificationService: PushNotificationService
  ) {}
}
